import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, Upload, 
  Image, Video, Eye, Heart, Crown, Loader2, Building2,
  MapPin, Phone, ExternalLink, ToggleLeft, ToggleRight,
  Users, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: "first_impression", label: "첫인상 매너" },
  { value: "date", label: "데이트 매너" },
  { value: "dining", label: "식사 매너" },
  { value: "conversation", label: "대화 매너" },
  { value: "emotion", label: "감정 매너" },
  { value: "contact", label: "연락 매너" },
  { value: "rejection", label: "거절 매너" },
];

const contentTypes = [
  { value: "video", label: "영상", icon: Video },
  { value: "article", label: "아티클", icon: Image },
];

export default function AdminMannerClassPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading } = useAuth();
  
  const [activeTab, setActiveTab] = useState("contents");
  const [contents, setContents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingSponsorImage, setUploadingSponsorImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    category: "first_impression",
    description: "",
    content_type: "article",
    media_url: "",
    thumbnail_url: "",
    tags: "",
    is_premium: false,
  });

  const [sponsorFormData, setSponsorFormData] = useState({
    title: "",
    category: "first_impression",
    description: "",
    image_url: "",
    link_url: "",
    contact: "",
    location: "",
    is_active: true,
  });

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchContents();
    fetchSponsors();
  }, [isAuthenticated, user, loading]);

  const fetchContents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/manner-class/contents`);
      const data = await response.json();
      setContents(data.contents || []);
    } catch (error) {
      console.error("Error fetching contents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 설명만 있고 본문(content)이 비어있는 항목을 LLM 으로 일괄 생성
  const handleGenerateContent = async () => {
    if (!window.confirm("설명만 있고 본문이 비어있는 매너클래스 항목을 AI로 자동 생성합니다. 진행할까요?")) return;
    setGeneratingContent(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/manner-class/generate-content`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "생성에 실패했습니다.");
      alert(`완료: ${data.updated ?? 0}개 콘텐츠가 생성되었습니다.${data.errors?.length ? "\\n(일부 실패: " + data.errors.length + "건)" : ""}`);
      await fetchContents();
    } catch (e) {
      alert(`오류: ${e.message}`);
    } finally {
      setGeneratingContent(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manner-class/sponsors`);
      const data = await response.json();
      setSponsors(data.sponsors || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const isMedia = type === "media";
    if (isMedia) setUploadingMedia(true);
    else setUploadingThumbnail(true);

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("media_type", file.type.startsWith("video") ? "video" : "image");

    try {
      const response = await fetch(`${API_URL}/api/admin/manner-class/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      
      if (isMedia) {
        setFormData(prev => ({ ...prev, media_url: data.url }));
      } else {
        setFormData(prev => ({ ...prev, thumbnail_url: data.url }));
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("파일 업로드에 실패했습니다.");
    } finally {
      if (isMedia) setUploadingMedia(false);
      else setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const contentData = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(t => t),
    };

    try {
      const url = editingContent
        ? `${API_URL}/api/admin/manner-class/contents/${editingContent.id}`
        : `${API_URL}/api/admin/manner-class/contents`;
      
      const response = await fetch(url, {
        method: editingContent ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contentData),
      });

      if (!response.ok) throw new Error("Failed to save");

      await fetchContents();
      resetForm();
    } catch (error) {
      console.error("Save error:", error);
      alert("저장에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin/manner-class/contents/${contentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      await fetchContents();
    } catch (error) {
      console.error("Delete error:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      category: content.category,
      description: content.description,
      content_type: content.content_type,
      media_url: content.media_url || "",
      thumbnail_url: content.thumbnail_url || "",
      tags: content.tags?.join(", ") || "",
      is_premium: content.is_premium || false,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingContent(null);
    setFormData({
      title: "",
      category: "first_impression",
      description: "",
      content_type: "article",
      media_url: "",
      thumbnail_url: "",
      tags: "",
      is_premium: false,
    });
  };

  // Sponsor handlers
  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const url = editingSponsor
        ? `${API_URL}/api/admin/manner-class/sponsors/${editingSponsor.id}`
        : `${API_URL}/api/admin/manner-class/sponsors`;
      
      const response = await fetch(url, {
        method: editingSponsor ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sponsorFormData),
      });

      if (!response.ok) throw new Error("Failed to save");

      await fetchSponsors();
      resetSponsorForm();
    } catch (error) {
      console.error("Save error:", error);
      alert("저장에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      const response = await fetch(
        `${API_URL}/api/admin/manner-class/sponsors/${sponsorId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      await fetchSponsors();
    } catch (error) {
      console.error("Delete error:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setSponsorFormData({
      title: sponsor.title,
      category: sponsor.category,
      description: sponsor.description,
      image_url: sponsor.image_url || "",
      link_url: sponsor.link_url || "",
      contact: sponsor.contact || "",
      location: sponsor.location || "",
      is_active: sponsor.is_active !== false,
    });
    setShowSponsorForm(true);
  };

  const resetSponsorForm = () => {
    setShowSponsorForm(false);
    setEditingSponsor(null);
    setSponsorFormData({
      title: "",
      category: "first_impression",
      description: "",
      image_url: "",
      link_url: "",
      contact: "",
      location: "",
      is_active: true,
    });
  };

  const handleSponsorImageUpload = async (file) => {
    if (!file) return;
    setUploadingSponsorImage(true);

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("media_type", "image");

    try {
      const response = await fetch(`${API_URL}/api/admin/manner-class/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      setSponsorFormData(prev => ({ ...prev, image_url: data.url }));
    } catch (error) {
      console.error("Upload error:", error);
      alert("파일 업로드에 실패했습니다.");
    } finally {
      setUploadingSponsorImage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== "admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-manner-class-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate("/manner-class")}
              className="flex items-center gap-2 text-stone-500 hover:text-burgundy-500 mb-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">매너 클래스로 돌아가기</span>
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-burgundy-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-stone-800">매너 클래스 관리</h1>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("contents")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "contents"
                ? "bg-burgundy-500 text-white"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            콘텐츠 관리 ({contents.length})
          </button>
          <button
            onClick={() => setActiveTab("sponsors")}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === "sponsors"
                ? "bg-burgundy-500 text-white"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            추천 강사/업체 ({sponsors.length})
          </button>
        </div>

        {/* Contents Tab */}
        {activeTab === "contents" && (
          <>
            {/* Add Button */}
            <div className="flex justify-end gap-2 mb-4">
              <Button
                onClick={handleGenerateContent}
                disabled={generatingContent}
                variant="outline"
                className="border-burgundy-300 text-burgundy-600 hover:bg-burgundy-50 flex items-center gap-2"
                data-testid="generate-content-btn"
              >
                {generatingContent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generatingContent ? "AI 생성 중..." : "AI 콘텐츠 일괄 생성"}
              </Button>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                새 콘텐츠 추가
              </Button>
            </div>

            {/* Content Form Modal */}
            {showForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-stone-800">
                      {editingContent ? "콘텐츠 수정" : "새 콘텐츠 추가"}
                    </h2>
                    <button
                      onClick={resetForm}
                      className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        제목 *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                        placeholder="콘텐츠 제목"
                      />
                    </div>

                    {/* Category & Type */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">
                          카테고리 *
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                        >
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">
                          콘텐츠 타입 *
                        </label>
                        <select
                          value={formData.content_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, content_type: e.target.value }))}
                          className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                        >
                          {contentTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        설명 *
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent resize-none"
                        placeholder="콘텐츠 설명"
                      />
                    </div>

                    {/* Media Upload */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        미디어 파일 (영상/이미지)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.media_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, media_url: e.target.value }))}
                          className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                          placeholder="URL 또는 파일 업로드"
                        />
                        <label className="px-4 py-3 bg-stone-100 hover:bg-stone-200 rounded-xl cursor-pointer flex items-center gap-2 transition-colors">
                          {uploadingMedia ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                          <input
                            type="file"
                            accept="image/*,video/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files[0], "media")}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Thumbnail Upload */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        썸네일 이미지
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.thumbnail_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                          className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                          placeholder="썸네일 URL"
                        />
                        <label className="px-4 py-3 bg-stone-100 hover:bg-stone-200 rounded-xl cursor-pointer flex items-center gap-2 transition-colors">
                          {uploadingThumbnail ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files[0], "thumbnail")}
                          />
                        </label>
                      </div>
                      {formData.thumbnail_url && (
                        <img
                          src={formData.thumbnail_url}
                          alt="Thumbnail preview"
                          className="mt-2 h-24 rounded-lg object-cover"
                        />
                      )}
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        태그 (쉼표로 구분)
                      </label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                        placeholder="예: 첫인상, 자신감, 커뮤니케이션"
                      />
                    </div>

                    {/* Premium Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gold-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-gold-500" />
                        <span className="font-medium text-stone-700">프리미엄 콘텐츠</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, is_premium: !prev.is_premium }))}
                        className={`p-1 rounded-full transition-colors ${
                          formData.is_premium ? "bg-gold-500" : "bg-stone-300"
                        }`}
                      >
                        {formData.is_premium ? (
                          <ToggleRight className="w-8 h-8 text-white" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-white" />
                        )}
                      </button>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetForm}
                        className="flex-1 py-3"
                      >
                        취소
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-burgundy-500 hover:bg-burgundy-600"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            {editingContent ? "수정" : "저장"}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Contents List */}
            {isLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
              </div>
            ) : contents.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">등록된 콘텐츠가 없습니다.</p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="mt-4 bg-burgundy-500 hover:bg-burgundy-600"
                >
                  첫 콘텐츠 추가하기
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">썸네일</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">제목</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">카테고리</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">타입</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">조회/좋아요</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-stone-500">프리미엄</th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-stone-500">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {contents.map((content) => (
                      <tr key={content.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-6 py-4">
                          {content.thumbnail_url ? (
                            <img
                              src={content.thumbnail_url}
                              alt={content.title}
                              className="w-16 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-16 h-10 bg-stone-200 rounded flex items-center justify-center">
                              <Image className="w-5 h-5 text-stone-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-stone-800 line-clamp-1">{content.title}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-burgundy-100 text-burgundy-600 text-xs rounded-full">
                            {categories.find(c => c.value === content.category)?.label || content.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-stone-600">
                            {content.content_type === "video" ? "영상" : "아티클"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3 text-sm text-stone-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {content.views || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" />
                              {content.likes || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {content.is_premium && (
                            <Crown className="w-5 h-5 text-gold-500" />
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(content)}
                              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4 text-stone-500" />
                            </button>
                            <button
                              onClick={() => handleDelete(content.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Sponsors Tab */}
        {activeTab === "sponsors" && (
          <>
            {/* Add Button */}
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowSponsorForm(true)}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                새 강사/업체 추가
              </Button>
            </div>

            {/* Sponsor Form Modal */}
            {showSponsorForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-stone-800">
                      {editingSponsor ? "강사/업체 수정" : "새 강사/업체 추가"}
                    </h2>
                    <button
                      onClick={resetSponsorForm}
                      className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSponsorSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        이름/업체명 *
                      </label>
                      <input
                        type="text"
                        required
                        value={sponsorFormData.title}
                        onChange={(e) => setSponsorFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                        placeholder="강사 이름 또는 업체명"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        전문 분야 *
                      </label>
                      <select
                        value={sponsorFormData.category}
                        onChange={(e) => setSponsorFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        소개 *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={sponsorFormData.description}
                        onChange={(e) => setSponsorFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent resize-none"
                        placeholder="강사/업체 소개"
                      />
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        대표 이미지
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={sponsorFormData.image_url}
                          onChange={(e) => setSponsorFormData(prev => ({ ...prev, image_url: e.target.value }))}
                          className="flex-1 px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                          placeholder="이미지 URL"
                        />
                        <label className="px-4 py-3 bg-stone-100 hover:bg-stone-200 rounded-xl cursor-pointer flex items-center gap-2 transition-colors">
                          {uploadingSponsorImage ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleSponsorImageUpload(e.target.files[0])}
                          />
                        </label>
                      </div>
                      {sponsorFormData.image_url && (
                        <img
                          src={sponsorFormData.image_url}
                          alt="Preview"
                          className="mt-2 h-24 rounded-lg object-cover"
                        />
                      )}
                    </div>

                    {/* Location & Contact */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">
                          위치
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                          <input
                            type="text"
                            value={sponsorFormData.location}
                            onChange={(e) => setSponsorFormData(prev => ({ ...prev, location: e.target.value }))}
                            className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                            placeholder="예: 서울 강남구"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">
                          연락처
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                          <input
                            type="text"
                            value={sponsorFormData.contact}
                            onChange={(e) => setSponsorFormData(prev => ({ ...prev, contact: e.target.value }))}
                            className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                            placeholder="02-1234-5678"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Link URL */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        홈페이지 URL
                      </label>
                      <div className="relative">
                        <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          type="url"
                          value={sponsorFormData.link_url}
                          onChange={(e) => setSponsorFormData(prev => ({ ...prev, link_url: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                          placeholder="https://example.com"
                        />
                      </div>
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                      <span className="font-medium text-stone-700">활성화 상태</span>
                      <button
                        type="button"
                        onClick={() => setSponsorFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                        className={`p-1 rounded-full transition-colors ${
                          sponsorFormData.is_active ? "bg-green-500" : "bg-stone-300"
                        }`}
                      >
                        {sponsorFormData.is_active ? (
                          <ToggleRight className="w-8 h-8 text-white" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-white" />
                        )}
                      </button>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetSponsorForm}
                        className="flex-1 py-3"
                      >
                        취소
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-burgundy-500 hover:bg-burgundy-600"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            {editingSponsor ? "수정" : "저장"}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Sponsors List */}
            {sponsors.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">등록된 강사/업체가 없습니다.</p>
                <Button
                  onClick={() => setShowSponsorForm(true)}
                  className="mt-4 bg-burgundy-500 hover:bg-burgundy-600"
                >
                  첫 강사/업체 추가하기
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sponsors.map((sponsor) => (
                  <div
                    key={sponsor.id}
                    className={`bg-white rounded-xl overflow-hidden shadow-sm border ${
                      sponsor.is_active ? "border-stone-200" : "border-red-200 opacity-60"
                    }`}
                  >
                    <div className="h-40 bg-stone-200 relative">
                      {sponsor.image_url ? (
                        <img
                          src={sponsor.image_url}
                          alt={sponsor.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-12 h-12 text-stone-400" />
                        </div>
                      )}
                      {!sponsor.is_active && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded">
                          비활성
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-stone-800">{sponsor.title}</h3>
                      <span className="text-xs text-burgundy-500 font-medium">
                        {categories.find(c => c.value === sponsor.category)?.label}
                      </span>
                      <p className="text-sm text-stone-500 mt-2 line-clamp-2">{sponsor.description}</p>
                      {sponsor.location && (
                        <p className="text-xs text-stone-400 mt-2 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {sponsor.location}
                        </p>
                      )}
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => handleEditSponsor(sponsor)}
                          className="flex-1 py-2 text-sm bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          수정
                        </button>
                        <button
                          onClick={() => handleDeleteSponsor(sponsor.id)}
                          className="py-2 px-3 text-sm bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
